import type {
  ApiClientOptions,
  ErrorState,
  JSONValue
} from 'common/api/apiClient';

// A real Error subclass (not a plain value) so throwing it satisfies
// `no-throw-literal`, callers get a stack trace for free (useful for the
// Honeybadger-reporting sites), and catch blocks can narrow with
// `instanceof` instead of an unchecked `as ApiRequestFailure` cast.
export class ApiRequestFailure extends Error {
  requestOptions: ApiClientOptions;
  responseJson: JSONValue;
  // Raw response body, for consumers that need it verbatim (e.g. a non-JSON
  // error page) rather than the best-effort `JSON.parse` in `responseJson`.
  // Empty when no response was ever received (`NO_INTERNET_CONNECTION`).
  responseText: string;
  errorState: ErrorState;
  // The response's HTTP status, or null when no response was received
  // (`NO_INTERNET_CONNECTION`). `errorState` collapses everything that is not
  // a 403 into `UNEXPECTED_ERROR`, so a caller that treats one status as a
  // state of its own -- a 404 meaning "not created yet" rather than a failure
  // -- reads it here.
  status: number | null;

  constructor(fields: {
    requestOptions: ApiClientOptions;
    responseJson: JSONValue;
    responseText: string;
    errorState: ErrorState;
    status?: number | null;
  }) {
    super(`apiClient request failed: ${fields.errorState}`);
    this.name = 'ApiRequestFailure';
    this.requestOptions = fields.requestOptions;
    this.responseJson = fields.responseJson;
    this.responseText = fields.responseText;
    this.errorState = fields.errorState;
    this.status = fields.status ?? null;
  }
}
